import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Textarea } from '../../../components/ui/textarea';
import { CustomFieldType, CustomFieldScope } from '../../types/custom-field';
import { toast } from 'react-toastify';

interface AddCustomFieldModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scope: CustomFieldScope;
  onAdd: (name: string, fieldType: CustomFieldType, dropdownOptions?: string[]) => Promise<void>;
}

const FIELD_TYPE_LABELS: Record<CustomFieldType, string> = {
  [CustomFieldType.TEXT]: 'Tekst',
  [CustomFieldType.NUMBER]: 'Nummer',
  [CustomFieldType.DROPDOWN]: 'Dropdown',
  [CustomFieldType.BOOLEAN]: 'Ja/Nee',
};

const AddCustomFieldModal: React.FC<AddCustomFieldModalProps> = ({
  open,
  onOpenChange,
  scope,
  onAdd,
}) => {
  const [name, setName] = useState('');
  const [fieldType, setFieldType] = useState<CustomFieldType>(CustomFieldType.TEXT);
  const [dropdownOptionsText, setDropdownOptionsText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleReset = () => {
    setName('');
    setFieldType(CustomFieldType.TEXT);
    setDropdownOptionsText('');
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error('Vul een naam in voor het veld');
      return;
    }

    if (fieldType === CustomFieldType.DROPDOWN) {
      const options = dropdownOptionsText
        .split('\n')
        .map((opt) => opt.trim())
        .filter((opt) => opt.length > 0);

      if (options.length === 0) {
        toast.error('Vul minimaal één dropdown optie in');
        return;
      }

      setIsSubmitting(true);
      try {
        await onAdd(name.trim(), fieldType, options);
        handleReset();
        onOpenChange(false);
      } catch (error) {
        // Error is handled in the hook
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setIsSubmitting(true);
      try {
        await onAdd(name.trim(), fieldType);
        handleReset();
        onOpenChange(false);
      } catch (error) {
        // Error is handled in the hook
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Nieuw aangepast veld toevoegen</DialogTitle>
          <DialogDescription>
            Voeg een nieuw aangepast veld toe aan {scope === CustomFieldScope.BUILDING ? 'gebouwen' : 'units'}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="field-name">Naam</Label>
            <Input
              id="field-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Bijv. Energielabel, Bouwjaar, etc."
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="field-type">Type</Label>
            <Select
              value={fieldType}
              onValueChange={(value) => setFieldType(value as CustomFieldType)}
              disabled={isSubmitting}
            >
              <SelectTrigger id="field-type">
                <SelectValue placeholder="Selecteer veldtype" />
              </SelectTrigger>
              <SelectContent
                position="popper"
                sideOffset={4}
                align="start"
                className="max-h-[200px]"
                {...{ modal: false } as any}
              >
                {Object.entries(FIELD_TYPE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {fieldType === CustomFieldType.DROPDOWN && (
            <div className="space-y-2">
              <Label htmlFor="dropdown-options">Dropdown opties (één per regel)</Label>
              <Textarea
                id="dropdown-options"
                value={dropdownOptionsText}
                onChange={(e) => setDropdownOptionsText(e.target.value)}
                placeholder="Optie 1&#10;Optie 2&#10;Optie 3"
                rows={6}
                disabled={isSubmitting}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Voer elke optie op een nieuwe regel in
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              handleReset();
              onOpenChange(false);
            }}
            disabled={isSubmitting}
          >
            Annuleren
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Bezig met toevoegen...' : 'Veld toevoegen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddCustomFieldModal;
